import { useState, useEffect } from "react"
import { Plus, Trash2, Check, X } from "lucide-react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"

import {
  Button,
  Card,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { PermissionButton } from "@/components/Auth"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"
import {
  AlertBox,
  DataTable,
  DataTableColumnType,
  OAuthProviderLogo,
} from "@/components"
import {
  ClientChannel,
  ClientChannelOAuthBinding,
  Workspace,
  Permission,
  BrandOAuthProvider,
} from "@/types"
import { useToast } from "@/hooks"
import {
  FetchStatus,
  oauthProvidersResponseToOAuthProviders,
  useGetBrandOAuthProvidersApi,
  useGetClientChannelOAuthBindingsApi,
  useCreateClientChannelOAuthBindingApi,
  useDeleteClientChannelOAuthBindingApi,
  oauthBindingsResponseToOAuthBindings,
} from "@/hooks/api"

export interface ChannelOAuthBindingsProps {
  channel: ClientChannel
  workspace: Workspace
}

export const ChannelOAuthBindings = ({
  channel,
  workspace,
}: ChannelOAuthBindingsProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()

  const [bindings, setBindings] = useState<ClientChannelOAuthBinding[]>([])
  const [isAdding, setIsAdding] = useState(false)

  // Form state for adding/editing
  const [deletingBinding, setDeletingBinding] =
    useState<ClientChannelOAuthBinding>()
  const [oauthProviders, setOAuthProviders] = useState<BrandOAuthProvider[]>([])

  // APIs
  const {
    data: oauthProvidersRespData,
    status: oauthProvidersRespStatus,
    fetchFn: getOAuthProviders,
  } = useGetBrandOAuthProvidersApi(
    workspace.organizationId || "",
    workspace.brand?.id || ""
  )

  const {
    data: bindingsRespData,
    error: bindingsRespError,
    fetchFn: getBindings,
  } = useGetClientChannelOAuthBindingsApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )

  const {
    data: deleteBindingRespData,
    error: deleteBindingRespError,
    fetchFn: deleteBinding,
  } = useDeleteClientChannelOAuthBindingApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id,
    deletingBinding?.id || ""
  )

  // Load bindings on mount
  useEffect(() => {
    if (workspace.id && channel.id) {
      getBindings({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
    }
  }, [workspace.id, channel.id, getBindings])

  // Handle bindings data
  useEffect(() => {
    if (!bindingsRespData || !bindingsRespData.data) return
    const bindingsData = oauthBindingsResponseToOAuthBindings(
      bindingsRespData.data
    )
    setBindings(bindingsData)
  }, [bindingsRespData])

  // Handle bindings error
  useEffect(() => {
    if (!bindingsRespError) return
    alertToast({
      title: t("workspace.channels.oauth.messages.error.loadBindings.title"),
      description: getUserFacingApiErrorMessage(
        bindingsRespError,
        t,
        t("workspace.channels.oauth.messages.error.loadBindings.description")
      ),
    })
  }, [bindingsRespError, alertToast, t])

  // Load OAuth providers
  useEffect(() => {
    if (workspace.brand?.id) {
      getOAuthProviders({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
    }
  }, [workspace.brand?.id, getOAuthProviders])

  // Handle OAuth providers data
  useEffect(() => {
    if (
      oauthProvidersRespStatus === FetchStatus.Success &&
      oauthProvidersRespData &&
      oauthProvidersRespData.data
    ) {
      setOAuthProviders(
        oauthProvidersResponseToOAuthProviders(oauthProvidersRespData.data)
      )
    }
  }, [oauthProvidersRespStatus, oauthProvidersRespData])

  const handleBindingCreated = () => {
    setIsAdding(false)

    getBindings({
      searchParams: new URLSearchParams({
        limit: "100",
        offset: "0",
      }),
    })
  }

  // Handle delete binding success
  useEffect(() => {
    if (!deleteBindingRespData) return
    setDeletingBinding(undefined)
    // Refetch bindings
    if (workspace.id && channel.id) {
      getBindings({
        searchParams: new URLSearchParams({
          limit: "100",
          offset: "0",
        }),
      })
    }
    successToast({
      title: t("workspace.channels.oauth.messages.deleted.title"),
      description: t("workspace.channels.oauth.messages.deleted.description"),
    })
  }, [
    deleteBindingRespData,
    workspace.id,
    channel.id,
    getBindings,
    successToast,
    t,
  ])

  // Handle delete binding error
  useEffect(() => {
    if (!deleteBindingRespError) return
    alertToast({
      title: t("workspace.channels.oauth.messages.error.deleteBinding.title"),
      description: getUserFacingApiErrorMessage(
        deleteBindingRespError,
        t,
        t("workspace.channels.oauth.messages.error.deleteBinding.description")
      ),
    })
  }, [deleteBindingRespError, alertToast, t])

  const handleBindingConfirmDelete = () => {
    if (!deletingBinding) return

    deleteBinding().catch(() => undefined)
  }

  const columns: DataTableColumnType<ClientChannelOAuthBinding>[] = [
    {
      title: t("workspace.channels.oauth.table.type"),
      fieldName: "oauthProviderType",
      width: "50px",
      headerClassName: "text-center",
      render: (binding) => (
        <div className="font-medium text-center">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="">
                  <OAuthProviderLogo
                    provider={binding.oauthProvider.provider}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {binding.oauthProvider.provider.toUpperCase()}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      title: t("workspace.channels.oauth.table.name"),
      fieldName: "oauthProviderName",
      width: "150px",
      render: (binding) => (
        <div className="font-medium">
          <span className="font-medium capitalize">
            {binding.oauthProvider.name}
          </span>
        </div>
      ),
    },
    {
      title: t("workspace.channels.oauth.table.created"),
      fieldName: "createdAt",
      width: "100px",
      render: (binding) => (
        <div className="text-sm text-muted-foreground">
          {format(binding.updatedAt, "yyyy-MM-dd HH:mm:ss")}
        </div>
      ),
    },
    {
      title: t("workspace.channels.oauth.table.actions"),
      width: "50px",
      headerClassName: "text-center",
      render: (binding) => (
        <div className="flex justify-center whitespace-nowrap">
          <PermissionButton
            permission={Permission.WsUpdateResources}
            workspace={workspace}
            disabledTooltip={t("common.noPermission")}
            size="sm"
            variant="ghost"
            onClick={() => setDeletingBinding(binding)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </PermissionButton>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-4 max-w-full overflow-hidden">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {t("workspace.channels.oauth.title")}
        </h3>
        <PermissionButton
          permission={Permission.WsUpdateResources}
          workspace={workspace}
          disabledTooltip={t("common.noPermission")}
          size="sm"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {t("workspace.channels.oauth.add")}
        </PermissionButton>
      </div>

      {isAdding && (
        <CreateOAuthBindingCard
          workspace={workspace}
          channel={channel}
          bindings={bindings}
          oauthProviders={oauthProviders}
          onCreate={handleBindingCreated}
          onCancel={() => setIsAdding(false)}
        />
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto w-full max-h-[500px]">
          <DataTable<ClientChannelOAuthBinding>
            data={bindings}
            columns={columns}
          />
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertBox
        open={Boolean(deletingBinding !== undefined)}
        onOpenChange={() => setDeletingBinding(undefined)}
        title={t("workspace.channels.oauth.delete.title")}
        description={t("workspace.channels.oauth.delete.description")}
        cancelText={t("workspace.channels.oauth.delete.cancel")}
        confirmText={t("workspace.channels.oauth.delete.confirm")}
        onConfirm={handleBindingConfirmDelete}
        onCancel={(e) => e.stopPropagation()}
      />
    </div>
  )
}

interface CreateOAuthBindingCardProps {
  workspace: Workspace
  channel: ClientChannel
  oauthProviders: BrandOAuthProvider[]
  bindings: ClientChannelOAuthBinding[]
  onCreate?: (provider: BrandOAuthProvider) => void
  onCancel?: () => void
}

const CreateOAuthBindingCard = ({
  workspace,
  channel,
  oauthProviders,
  bindings,
  onCreate,
  onCancel,
}: CreateOAuthBindingCardProps) => {
  const { t } = useTranslation()
  const { alertToast, successToast } = useToast()

  const [selectedProvider, setSelectedProvider] = useState<BrandOAuthProvider>()

  // APIs
  const {
    data: createBindingRespData,
    error: createBindingRespError,
    fetchFn: createBinding,
  } = useCreateClientChannelOAuthBindingApi(
    workspace.organizationId || "",
    workspace.id,
    channel.id
  )

  const handleAddBinding = () => {
    if (!selectedProvider) {
      alertToast({
        title: t("workspace.channels.oauth.messages.error.addBinding.title"),
        description: t(
          "workspace.channels.oauth.messages.error.addBinding.required"
        ),
      })
      return
    }

    createBinding({
      jsonBody: {
        oauth_provider_id: selectedProvider.id,
      },
    }).then(() => {
      onCreate?.(selectedProvider)
    })

    setSelectedProvider(undefined)
  }

  const handleSelectProvider = (value: string) => {
    const provider = oauthProviders.find((p) => p.id === value)
    if (!provider) return

    if (bindings.some((b) => b.oauthProvider.provider === provider.provider)) {
      alertToast({
        title: t(
          "workspace.channels.oauth.messages.error.selectProvider.title"
        ),
        description: t(
          "workspace.channels.oauth.messages.error.selectProvider.alreadyBound"
        ),
      })
      return
    }
    setSelectedProvider(provider)
  }

  // Handle create binding success
  useEffect(() => {
    if (!createBindingRespData || !createBindingRespData.data) return
    successToast({
      title: t("workspace.channels.oauth.messages.added.title"),
      description: t("workspace.channels.oauth.messages.added.description"),
    })
  }, [createBindingRespData, workspace.id, channel.id, successToast, t])

  // Handle create binding error
  useEffect(() => {
    if (!createBindingRespError) return

    alertToast({
      title: t("workspace.channels.oauth.messages.error.addBinding.title"),
      description: getUserFacingApiErrorMessage(
        createBindingRespError,
        t,
        createBindingRespData?.message ||
          t("workspace.channels.oauth.messages.error.addBinding.description")
      ),
    })
  }, [createBindingRespError, createBindingRespData, alertToast, t])

  return (
    <Card className="p-4">
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[200px] space-y-2">
          <Label>{t("workspace.channels.oauth.form.providerLabel")}</Label>
          <Select
            value={selectedProvider?.id || ""}
            onValueChange={handleSelectProvider}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={t(
                  "workspace.channels.oauth.form.providerPlaceholder"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {oauthProviders.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  <div className="flex items-center gap-2">
                    <OAuthProviderLogo provider={provider.provider} />
                    <span className="font-medium capitalize">
                      {provider.name}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-0">
          <Button variant="ghost" size="icon" onClick={handleAddBinding}>
            <Check className="h-4 w-4 text-green-500" />
          </Button>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default ChannelOAuthBindings
